// AiAgentStepper — Floating panel som visar status för en pågående eller
// avslutad agent-pipeline (planner → critic → builder → layout → reviewer).
// Pure presentational — ingen state-management här; caller passerar
// status-array från runPipeline-report.

import { memo } from 'react';
import { CheckCircle2, Loader2, AlertCircle, Circle } from 'lucide-react';

export type AgentStepStatus = 'pending' | 'running' | 'ok' | 'failed' | 'skipped';

export interface AgentStepState {
  agentId: string;
  label: string;
  status: AgentStepStatus;
  /** Credits förbrukade (eller estimerade om pending). */
  cost?: number;
  /** Fel-meddelande för failed. */
  error?: string;
  /** Kort summary (t.ex. "4 sheets, 32 blocks"). */
  summary?: string;
}

interface Props {
  steps: AgentStepState[];
  /** Visa samlad totalkostnad längst ner. */
  totalCost?: number;
  className?: string;
}

function statusIcon(status: AgentStepStatus) {
  switch (status) {
    case 'ok':
      return <CheckCircle2 className="w-4 h-4 text-green-500" aria-label="Klart" />;
    case 'running':
      return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" aria-label="Körs" />;
    case 'failed':
      return <AlertCircle className="w-4 h-4 text-destructive" aria-label="Fel" />;
    case 'skipped':
      return <Circle className="w-4 h-4 text-muted-foreground opacity-50" aria-label="Hoppad" />;
    default:
      return <Circle className="w-4 h-4 text-muted-foreground" aria-label="Väntar" />;
  }
}

function statusClass(status: AgentStepStatus): string {
  switch (status) {
    case 'ok': return 'text-foreground';
    case 'running': return 'text-foreground font-medium';
    case 'failed': return 'text-destructive';
    case 'skipped': return 'text-muted-foreground';
    default: return 'text-muted-foreground';
  }
}

export const AiAgentStepper = memo(function AiAgentStepper({ steps, totalCost, className }: Props) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={`border border-border rounded-md bg-card p-3 text-sm space-y-2 ${className ?? ''}`}
    >
      <div className="text-xs text-muted-foreground uppercase tracking-wide">Agent-pipeline</div>
      <ol className="space-y-1.5">
        {steps.map((step) => (
          <li key={step.agentId} className={`flex items-start gap-2 ${statusClass(step.status)}`}>
            <span className="shrink-0 mt-0.5">{statusIcon(step.status)}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span>{step.label}</span>
                {step.cost !== undefined && (
                  <span className="text-xs text-muted-foreground shrink-0">
                    {step.status === 'pending' ? '~' : ''}{step.cost}c
                  </span>
                )}
              </div>
              {step.summary && (
                <div className="text-xs text-muted-foreground truncate">{step.summary}</div>
              )}
              {step.error && (
                <div className="text-xs text-destructive mt-0.5">{step.error}</div>
              )}
            </div>
          </li>
        ))}
      </ol>
      {totalCost !== undefined && (
        <div className="pt-2 border-t border-border flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Total kostnad</span>
          <span className="font-medium">{totalCost} credits</span>
        </div>
      )}
    </div>
  );
});
